/**
 * The permission catalogue.
 *
 * Every capability the product enforces is named here, once. A module adds its
 * permissions to this file when it ships; nothing else has to change, because
 * the check sites are generic.
 *
 * Naming is `module.resource.action` or `module.action`. The prefix is the
 * module that owns the permission, which keeps the catalogue readable as it
 * grows and makes it obvious when a module is reaching outside itself.
 *
 * See ADR-0013.
 */
export const PERMISSIONS = [
    'organization.read',
    'organization.write',
    'catalog.read',
    'catalog.write',
    'party.read',
    /**
     * Registering somebody new.
     *
     * Separate from `party.write` because the counter case and the back-office
     * case are genuinely different: an operator with a customer standing in
     * front of them must be able to create a record, and must not be able to
     * rewrite the document, addresses or credit standing of an account that
     * already exists. Without the split every role held `party.write`, which
     * made it a permission that granted nothing and could not be tested.
     */
    'party.create',
    'party.write',
    /**
     * Ver os marcadores que a loja criou — de cliente e de conversa (F088).
     *
     * Concedida a TODOS os papéis, e a leitura mais barata do produto: um
     * marcador é um rótulo que já aparece pendurado em cada cliente e em cada
     * conversa que a pessoa pode ver. Esconder a lista de rótulos de quem já vê
     * os rótulos colados não protegeria nada — só impediria o seletor de
     * funcionar.
     */
    'tag.read',
    /**
     * Criar, renomear, mudar o tom e arquivar um marcador.
     *
     * Separada de `.read` pelo mesmo motivo de `communication.queue.read`/
     * `.write`: colar um marcador existente num cliente é trabalho de quem
     * atende; decidir QUAIS marcadores a loja usa é vocabulário da operação
     * inteira, e um marcador renomeado muda o que duzentos cadastros dizem de
     * uma vez.
     *
     * COLAR não exige esta permissão: marcar um cliente pede `party.write`, e
     * marcar uma conversa pede `communication.conversation.assign` — as
     * permissões que já dizem "esta pessoa opera este cadastro" e "esta pessoa
     * organiza o atendimento". Uma terceira permissão para cada família seria
     * uma matriz que ninguém consegue revisar.
     */
    'tag.manage',
    'pricing.read',
    'pricing.write',
    'inventory.read',
    'inventory.write',
    /**
     * Correcting a balance against a count, rather than recording a movement
     * that happened.
     *
     * Separate from `inventory.write` because an adjustment is the one operation
     * that can make stock say whatever somebody wants it to say. Section 14
     * requires the ledger to be auditable, and that is only meaningful if the
     * ability to overrule it is granted deliberately.
     */
    'inventory.adjust',
    'sales.read',
    'sales.write',
    /** Undoing a confirmed sale, which reverses stock and money. */
    'sales.cancel',
    /**
     * Alterar o que já saiu das mãos de quem fez (F147).
     *
     * O orçamento de OUTRO vendedor, o pedido que foi com o motoboy e a venda
     * paga. O vendedor altera o orçamento dele com `sales.write`; mexer no que
     * outra pessoa prometeu, ou no que já tirou mercadoria da loja, é do dono, do
     * admin e do gerente — a decisão da Elite Digital.
     */
    'sales.amend',
    /**
     * Receber o sinal de um pedido confirmado (F173).
     *
     * Do vendedor também, por decisão do dono: na 4Play o sinal chega por PIX na
     * conversa com o cliente, e é quem atende que lança. Continua exigindo o
     * caixa aberto da filial — o dinheiro entra no caixa do dia. Nenhuma tela a
     * declara: negar uma aba não tira do vendedor o sinal.
     */
    'sales.deposit',
    /**
     * A conferência da venda (26/09): o estoquista pega a peça e confere, a
     * caixa confere de novo, e cada um grava a hora — para o dono puxar a
     * câmera quando o cliente diz que a peça não saiu.
     *
     * Uma permissão por etapa: o trocador faz a do estoque e NÃO a do caixa, que
     * é a segunda conferência — quem pegou a peça não confere a si mesmo.
     * Nenhuma tela as declara: seguem o papel.
     */
    'sales.check.stock',
    'sales.check.cashier',
    /** Corrigir a hora de uma conferência, com motivo e guardando a original. */
    'sales.check.correct',
    /**
     * Selling at a price the price list did not produce.
     *
     * Not `pricing.write`, which is maintaining the tables everybody sells from —
     * this is departing from them for one line of one sale, which is a different
     * act with a different blast radius and usually a different person: the
     * manager standing at the counter, not whoever maintains the catalogue.
     *
     * It exists because the capability already did. `/pos/checkout` and
     * `/documents` have accepted whatever `unitPrice` the client sent since F016
     * and F013, with `priceBasis: 'manual'` as the default — so an operator was
     * one devtools console away from selling at R$ 0,01, and nothing recorded
     * that the price had been departed from at all. Section 28 puts the rule on
     * the server; this is the name it is enforced under.
     */
    'sales.price.override',
    /**
     * Ver trocas e vales (F126) — a fila, o comprovante, o histórico de uma venda.
     */
    'exchange.read',
    /**
     * Pedir uma troca ou um vale ao trocador.
     *
     * O caixa e o vendedor atendem o cliente que voltou com a peça, mas não
     * decidem: pedir é registrar o que o cliente quer, e quem mexe no estoque e
     * no saldo é outra pessoa. É a mesma separação do balcão (ADR-0027) — quem
     * promete não é quem entrega.
     */
    'exchange.request',
    /**
     * Criar, aceitar, recusar e concluir troca e vale (F126).
     *
     * É o que devolve peça ao estoque, dá baixa na peça que "já era" e cria saldo
     * que o cliente gasta como dinheiro. Por isso é permissão própria e não
     * `sales.cancel`: cancelar desfaz uma venda inteira; trocar decide, peça por
     * peça, o que a loja aceita de volta.
     */
    'exchange.manage',
    /**
     * Aprovar troca ou vale de produto que o Aeris não vendeu (F148).
     *
     * A loja assumida de outro dono: o cliente comprou lá, antes. Sem venda não
     * há preço pago nem limite do que já voltou, então quem digita a senha
     * aprova peça e valor. Dono, admin e gerente — e NÃO o trocador, que já tem
     * `exchange.manage` e se autorizaria sozinho.
     */
    'exchange.without_sale',
    /**
     * Liberar uma peça que JÁ voltou a voltar de novo (F178).
     *
     * A Elite trocou a mesma tela duas vezes: o servidor recusa
     * (`already-returned`), e o dono pediu que o gerente consiga liberar mesmo
     * assim — devolução sem nota, peça trocada por engano em duas vendas. Dono,
     * admin e gerente, e NÃO o trocador, pela mesma razão da de cima: ele já tem
     * `exchange.manage` e se autorizaria sozinho.
     */
    'exchange.repeat_return',
    /**
     * Dar e tirar vale na mão, fora de uma troca (F216).
     *
     * O dono: "o admin quer conseguir editar os vales, dar vale, subtrair vale,
     * na mão, com a senha dele". É mexer direto no que a loja deve ao cliente —
     * sem peça, sem venda —, então só dono e admin, e sempre com a senha de quem
     * autoriza (`/auth/authorize`), mesmo que seja ele mesmo quem está logado.
     */
    'exchange.credit.adjust',
    'purchasing.read',
    'purchasing.write',
    /**
     * Recording a delivery: stock in, with cost.
     *
     * Separate from `purchasing.write` because writing a purchase order is a
     * plan, and receiving is the moment goods and money actually enter the
     * business — it moves the ledger and rewrites what every product is worth.
     * The person who orders and the person who counts what arrived are usually
     * not the same person, and should not have to be.
     */
    'purchasing.receive',
    'finance.read',
    'finance.write',
    /**
     * As rotas de entrega (F265–F267, chave `entregas-rota`): ver a página.
     */
    'delivery.route.read',
    /**
     * Montar e despachar a rota, localizar o cliente no mapa e dizer de onde as
     * entregas saem (F265–F267).
     *
     * Separada de `party.write` de propósito: o caixa não reescreve cadastro,
     * mas precisa pôr o ponto no endereço do lojista que vai receber. A rota de
     * localizar só põe ponto num endereço que existe ou acrescenta um endereço
     * de entrega — nunca muda o texto de um endereço.
     */
    'delivery.route',
    /**
     * Recording a baixa: money actually paid or received against a título.
     *
     * Separate from `finance.write` for the reason `purchasing.receive` is
     * separate from `purchasing.write`: writing a title is recording an
     * agreement, and settling one asserts that money moved. The person who
     * enters the shop's bills and the person who pays them are not the same
     * person in any business large enough to care, and separating them is the
     * oldest control in accounting.
     */
    'finance.settle',
    /**
     * Granting, changing and suspending a customer's credit.
     *
     * Its own permission, not `finance.write`: deciding how much a customer may
     * owe is a commercial decision about a relationship, and recording a bill is
     * clerical. In most shops the first belongs to whoever answers for the losses
     * and the second does not.
     */
    'finance.credit.manage',
    /**
     * Cobrar (F135): registrar a ligação, a mensagem e a promessa de pagamento
     * de um cliente, e mandar a cobrança pelo WhatsApp.
     *
     * Owner, admin, manager e o caixa. O caixa já recebe e acerta o motoboy
     * (`finance.settle`); ligar para quem deve é o mesmo trabalho visto um dia
     * antes. Renegociar NÃO vem com esta: muda o que o cliente deve, e é
     * `finance.write`.
     */
    'finance.collect',
    /** Working a till: opening it, selling, closing it. */
    'pos.operate',
    /**
     * Overseeing tills that are not yours.
     *
     * Closing somebody else's session, seeing the difference between counted and
     * expected cash, and authorising a withdrawal. Separate from `pos.operate`
     * because an operator who can reconcile their own shortfall is an operator
     * whose shortfall is never discovered.
     */
    'pos.supervise',
    'payment.read',
    'payment.write',
    /** Which payment methods a tenant accepts, and their settlement rules. */
    'payment.method.manage',
    'identity.user.read',
    'identity.user.write',
    'identity.session.revoke',
    /**
     * Sales channels: the marketplaces a shop lists on, and which product is
     * which listing.
     *
     * Read is separate from write because the mapping is the interesting half.
     * Someone who can change which Aeris product a marketplace listing points at
     * can, with one edit, make the wrong thing ship — and can change the stock
     * buffer, which is the one number standing between the counter and an
     * oversell. Granted to owner, admin and manager; NOT to the operator, who
     * needs none of it to work a till.
     */
    'channel.read',
    'channel.write',
    /**
     * Sync a branch's Edge: pull the catalogue down, push what it sold up.
     *
     * Its own permission rather than a bundle of the six reads it performs,
     * because it is one capability with one meaning — "this credential belongs
     * to a shop's Edge" — and because the Edge should be grantable to a
     * device without also being grantable to a person by accident.
     *
     * Today it is checked against a user session, which is not good enough for
     * a shop; see docs/features/F017-offline-pos.md on Edge authentication. The
     * permission existing now is what makes that change an implementation
     * rather than a redesign.
     */
    'edge.sync',
    /**
     * Emitir e revogar as credenciais das máquinas que rodam o Edge (F041).
     *
     * Separada de `edge.sync` de propósito, e a separação é a feature: se a
     * credencial que sincroniza pudesse cunhar credenciais, um Edge roubado
     * emitiria uma nova para si mesmo e a revogação viraria teatro. Quem
     * administra as máquinas é gente, numa tela, com sessão.
     */
    'edge.manage',
    /**
     * Ver as contas de canal do atendimento (WhatsApp, hoje) e o que chegou
     * por elas.
     *
     * Separada de `communication.channel.write` pela mesma razão de
     * `channel.read`/`channel.write`: uma conta de canal carrega um segredo
     * (a service key do gateway) e decide de que número a loja atende — ver
     * quem está cadastrado é uma coisa, poder reconectar ou desligar um número
     * é outra, com um raio de efeito bem maior.
     */
    'communication.channel.read',
    /**
     * Cadastrar, editar e desconectar uma conta de canal.
     *
     * Concedida a owner e admin. NÃO ao manager: filas, atendentes e o robô —
     * o que um manager de fato administra neste módulo — chegam nas features
     * seguintes com suas próprias permissões; a conta em si, com sua
     * credencial cifrada, é decisão de quem responde pela conta.
     */
    'communication.channel.write',
    /**
     * Ler o inbox: as conversas e as mensagens trocadas por elas (F046).
     *
     * Separada de `communication.channel.read` porque são leituras de
     * naturezas diferentes — uma é configuração (o número, a credencial), a
     * outra é o conteúdo de uma conversa com um cliente. Concedida também a
     * `cashier` e `salesperson`: é o pedido do dono — quem atende no
     * WhatsApp é um vendedor ou um caixa, o mesmo balcão que já vende.
     */
    'communication.conversation.read',
    /**
     * Responder uma conversa: mandar uma mensagem de texto livre.
     *
     * O envio aplica o prefixo `*Nome:*` com o nome de quem está logado
     * (`prefixedBody` em `@aeris/communication`) e recusa fora da janela de
     * 24h (`canSendFreeform`) — a permissão só decide QUEM pode tentar; a
     * janela decide se o Aeris aceita a tentativa. Mesmos papéis de
     * `communication.conversation.read`: ler e responder são o mesmo
     * trabalho de atendimento, e seria estranho conceder um sem o outro.
     */
    'communication.message.send',
    /**
     * Gerar um Pix pelo atendimento (F204). Cobra em nome da loja, então é uma
     * permissão própria, e não carona de "mandar mensagem": o dono pode deixar
     * alguém atender sem deixar cobrar.
     */
    'pix.charge.create',
    /**
     * Receber o aviso de Pix pago e marcá-lo como visto (F205). O sistema não
     * dá baixa em nada: quem recebe o aviso é o caixa, que dá a baixa.
     */
    'pix.charge.notify',
    /** Ver os números do Pix: quanto entrou, por dia, vendedor e filial (F205). */
    'pix.charge.read',
    /**
     * Decidir quem é o cliente do outro lado de um `wa_id` (F048): confirmar
     * um vínculo ambíguo, dizer "este número não é de cliente" (ignorar), ou
     * cadastrar uma pessoa nova a partir da conversa.
     *
     * Não separada de `communication.conversation.read`/`.message.send` por
     * acidente — é a mesma decisão de sempre neste módulo: quem atende no
     * WhatsApp (owner, admin, manager, cashier, salesperson) é quem sabe
     * responder "quem é esse número?", porque é quem está lendo a conversa.
     * O matching AUTOMÁTICO (inequívoco, `auto_matched`) não passa por aqui —
     * roda no worker, sem sessão; esta permissão é só para a decisão humana
     * que o matching ambíguo ou ausente deixa pendente.
     */
    'communication.contact.match',
    /**
     * Ver as filas de atendimento: nome, horário, robô-primeiro e quem é
     * membro de cada uma (F047).
     *
     * Separada de `.write` pela mesma razão de `communication.channel.read`/
     * `.write`: um atendente que trabalha numa fila precisa saber quais
     * existem para escolher uma na hora de assumir uma conversa; reconfigurar
     * horário e membros é outra decisão, com outro raio de efeito.
     */
    'communication.queue.read',
    /**
     * Criar, editar e gerenciar membros de uma fila.
     *
     * Concedida a owner, admin e manager — quem administra a operação da
     * loja, não quem atende nela. Um `cashier`/`salesperson` vira atendente de
     * uma fila sendo adicionado como MEMBRO (`communication_queue_member`),
     * nunca por ganhar esta permissão: não existe papel `attendant` novo, e a
     * decisão de quem atende por qual fila é administrativa, não do próprio
     * atendente.
     */
    'communication.queue.write',
    /**
     * Escolher quais grupos do WhatsApp entram no Atendimento e ligar cada um à
     * loja do cliente (F268). Owner, admin e manager: é o gerente quem sabe
     * qual grupo é de qual lojista, e acompanhar um grupo passa a gravar tudo o
     * que se fala nele.
     */
    'communication.group.manage',
    /**
     * Atribuir uma conversa a um atendente, ou assumi-la ("é minha") — F047.
     *
     * Concedida aos mesmos cinco papéis de `communication.conversation.read`/
     * `.message.send`: quem atende no WhatsApp decide quem responde, do mesmo
     * jeito que já decide o que responder. Cobre atribuir livremente, assumir
     * uma conversa sem dono, e confirmar a que já é sua.
     *
     * **TOMAR de outra pessoa é diferente, e exige mais.** A regra pura
     * (`canAssignConversation`, `@aeris/communication`) só libera esse caso
     * quando o ator TAMBÉM tem `communication.queue.write` — reaproveitada
     * como o "supervisiona esta operação" em vez de uma terceira permissão
     * nova: tirar uma conversa de um colega sem consultá-lo é um ato do
     * mesmo tamanho que reconfigurar a fila em si, não do tamanho de
     * atender. Fica auditado como `communication.conversation.taken`
     * (distinto de `.assigned`) precisamente porque é o caso em que alguém
     * PERDE o que tinha — ver docs/features/F047-queues-and-realtime.md.
     */
    'communication.conversation.assign',
    /**
     * Ver os chamados: protocolo, assunto, situação, prioridade, responsável e
     * o histórico de cada um (F051).
     *
     * Concedida aos cinco papéis operacionais — a mesma lista de
     * `communication.conversation.read`: um chamado é o atendimento que não
     * acabou na conversa, e quem já lê o inbox precisa poder ver o que ficou
     * pendente.
     */
    'communication.ticket.read',
    /**
     * Abrir, mudar situação, atribuir, anotar, fechar e reabrir um chamado.
     *
     * Concedida aos mesmos cinco papéis — **inclusive `cashier` e
     * `salesperson`**, de propósito: é o pedido do dono, "chamados" no mesmo
     * balcão que já vende e atende no WhatsApp. Não é `communication.queue.write`
     * (administração de fila): abrir um chamado é o trabalho de quem está
     * atendendo agora, não uma decisão de configuração de loja.
     */
    'communication.ticket.write',
    /**
     * Ver os modelos aprovados pela Meta: categoria, situação, o motivo da
     * recusa quando houver (F050).
     *
     * Concedida aos cinco papéis operacionais — a mesma lista de
     * `communication.conversation.read`: fora da janela de 24h só um modelo
     * aprovado pode ser enviado, e quem atende precisa ver quais existem para
     * escolher um.
     */
    'communication.template.read',
    /**
     * Criar um modelo (submetê-lo à aprovação da Meta).
     *
     * Restrita a owner/admin/manager — quem administra a operação da loja, não
     * quem atende nela. Um modelo mal escrito volta recusado da Meta em dias,
     * e a categoria atribuída na aprovação decide o CUSTO de toda campanha que
     * o usar depois (o rework de cobrança de 01/10/2026) — não é decisão de
     * balcão.
     */
    'communication.template.manage',
    /**
     * Ver a configuração do robô de IA: ligado/desligado, ordem de
     * provedores, modelo por provedor, horário humano, palavras de handoff,
     * tetos, e o histórico de sessões com o gasto do período (F052).
     *
     * Restrita a owner/admin/manager — a mesma lista de
     * `communication.channel.read`, não a lista de cinco papéis operacionais
     * de `communication.conversation.read`. Diferente de um chamado ou de uma
     * conversa, isto não é trabalho de balcão: é a configuração de COMO a
     * loja fala com o cliente quando ninguém está olhando, e quanto isso
     * custa — cashier e salesperson atendem através do robô, não decidem o
     * que ele diz.
     */
    'communication.bot.read',
    /**
     * Ligar/desligar o robô, mudar prompt, saudação, ordem de provedores,
     * modelo, horário humano, palavras de handoff, tetos, e cadastrar a
     * chave de cada provedor.
     *
     * Restrita a owner/admin/manager — quem mexe aqui mexe no que o negócio
     * diz aos clientes e no quanto gasta (a mesma frase do dono na spec da
     * F052). Uma chave de API mal cadastrada, ou um teto de custo alto
     * demais, é um erro que se propaga por toda conversa que o robô tocar —
     * não é decisão de quem está atendendo um cliente agora.
     */
    'communication.bot.manage',
    /**
     * Ver as regras de follow-up: o que dispara, o prazo, o modelo, e quantas
     * tarefas cada regra tem pendentes agora com a próxima data (F053).
     *
     * Restrita a owner/admin/manager — a mesma lista de `communication.bot.
     * read`, não a lista de cinco papéis operacionais de `communication.
     * conversation.read`. "Comprou ar-condicionado → lembrete de manutenção em
     * 6 meses" é uma decisão de como a loja fala com o cliente meses depois de
     * uma venda, não trabalho de quem está atendendo agora.
     */
    'communication.followup.read',
    /**
     * Criar, ligar/desligar e editar uma regra de follow-up.
     *
     * Restrita a owner/admin/manager, pelo mesmo raciocínio de
     * `communication.bot.manage`: uma regra mal configurada manda mensagem
     * errada para um cliente meses depois de uma venda, sem ninguém do balcão
     * ter decidido isso naquele instante.
     */
    'communication.followup.manage',
    /**
     * Ver as campanhas: segmento, ritmo, situação e contadores de cada uma
     * (F054).
     *
     * Restrita a owner/admin/manager — a mesma lista de
     * `communication.followup.read`: "pegar todos os clientes que compraram X
     * e mandar mensagem de reajuste" é uma decisão de como a loja fala com
     * uma base inteira de clientes de uma vez, não trabalho de balcão.
     */
    'communication.campaign.read',
    /**
     * Criar, agendar, iniciar, pausar, retomar e cancelar uma campanha.
     *
     * Restrita a owner/admin/manager, pelo mesmo raciocínio de
     * `communication.followup.manage` — e por um motivo a mais: uma campanha
     * mal configurada (ritmo alto demais, segmento errado) compete pelo MESMO
     * teto de mensagens por segundo que o atendimento ao vivo usa.
     */
    'communication.campaign.manage',
    /**
     * Ver o dashboard de custo: mensagens e gasto por categoria e por
     * período, comparado com o que a Meta relatar (F054).
     *
     * Restrita a owner/admin/manager — quanto a loja gasta com a API é uma
     * pergunta financeira, da mesma família de `finance.read`, não uma
     * pergunta de quem está atendendo um cliente agora.
     */
    'communication.cost.read',
    /**
     * Ver o painel da loja no Início (F134): faturamento por período, por
     * vendedor e por forma de pagamento, o funil de orçamentos, quem está
     * devendo, os vales em aberto, o estoque abaixo do mínimo e o que ficou
     * esquecido.
     *
     * Restrita a owner/admin/manager. `sales.read` e `finance.read` não servem
     * de porta: o caixa e o vendedor também as têm, e o painel diz quanto CADA
     * vendedor vendeu — uma pergunta de quem dirige a loja, não de quem está no
     * balcão ao lado do colega.
     */
    'dashboard.read',
    /**
     * Abrir a tela de Relatórios (F158): top clientes, top produtos, trocas por
     * marca / produto / motivo, e vendas por vendedor.
     *
     * Owner, admin e gerente — hoje **exatamente a mesma audiência** de
     * `dashboard.read`, então ninguém ganha nem perde nada ao ela nascer.
     *
     * Separada mesmo assim porque o próprio pedido do dono já contém a
     * divergência: *"quais marcas mais têm troca"* é pergunta de comprador, e o
     * trocador é quem tem a peça na mão. Com uma permissão só, dar a ele o
     * relatório de trocas daria junto quanto cada vendedor vendeu — que é
     * exatamente o que `dashboard.read` existe para não fazer. Separar agora
     * custa uma linha; separar depois custa mudar o que já foi concedido.
     */
    'report.read',
    /**
     * Comissão dos vendedores (F137): a tela Comissões, com a prévia e os
     * extratos de TODOS. Owner, admin e gerente — a mesma pergunta de quem dirige
     * que o painel da loja responde.
     */
    'commission.read',
    /**
     * Definir a % e quem recebe, fechar o mês (o que gera título a pagar) e
     * desfazer um fechamento. Owner e admin: o fechamento lança dinheiro.
     */
    'commission.manage',
    /**
     * Ver a PRÓPRIA comissão no Início. Todos os papéis: quem vende quer saber
     * quanto vai receber, e isso não dá acesso à comissão de ninguém mais.
     */
    'commission.read.own',
    /**
     * A folha e o caderno de gastos (F146): ver a folha do mês, os gastos e o
     * resumo. Decisão do dono: a menina do caixa vê e preenche, então o caixa
     * tem — além de owner, admin e gerente. Vendedor e trocador, não.
     */
    'expense.read',
    /** Lançar um gasto e marcar um pagamento de salário. Os mesmos papéis. */
    'expense.write',
    /**
     * Criar categoria e desfazer um lançamento de gasto. Owner, admin e gerente.
     *
     * Cadastrar funcionário e definir quanto ele ganha SAIU daqui na F157: virou
     * `payroll.manage`, porque é salário.
     */
    'expense.manage',
    /**
     * Ver a FOLHA: quanto cada funcionário ganha, o que já foi pago, quanto
     * falta — F157.
     *
     * Nasceu separada de `expense.read` porque as duas coisas da mesma tela têm
     * sigilos diferentes. Decisão do dono (16/09): *"o caderno de gastos fica
     * como está; a folha tranca"*. Só owner e admin.
     *
     * Até aqui o caixa e o gerente enxergavam o salário de todo mundo, porque a
     * F146 pôs as duas na mesma permissão — *"a menina do caixa vê e preenche"*
     * valia para o caderno, e levou a folha junto.
     */
    'payroll.read',
    /**
     * Cadastrar funcionário, definir salário e bônus, lançar bônus ou desconto,
     * e marcar o pagamento do salário — F157. Owner e admin.
     *
     * Quem não pode VER a folha não pode pagá-la: pagar exige saber quanto.
     */
    'payroll.manage',
    /**
     * Ver a fila de produção de uniformes e a ficha de cada pedido (F170).
     *
     * Só existe para a conta que tem a chave `uniformes`: a página que a declara
     * leva `feature`, e sem a chave a permissão some de `effectivePermissions`
     * junto com a tela. Owner, admin, gerente e vendedor — quem é só da produção
     * recebe `salesperson` e o admin tira o resto pela F038.
     */
    'uniforms.read',
    /**
     * Trocar a etapa, editar a ficha, a grade e os personalizados, subir a arte
     * e repetir um pedido (F170). Os mesmos papéis de `.read`.
     *
     * **Nenhuma página a declara**, de propósito: negar a aba de impressoras a
     * alguém não pode tirar dele a troca de etapa, e `effectivePermissions` tira
     * a permissão quando a última página que a declara é negada. Por isso ela
     * não some com a chave desligada — quem barra é `requireFeature` em cada
     * rota.
     */
    'uniforms.write',
    /**
     * O cadastro das impressoras da produção (F170). Owner, admin e gerente:
     * é vocabulário da operação inteira, como marcas e marcadores.
     */
    'uniforms.manage',
    /**
     * A ordem de serviço da assistência técnica (F321, ADR-0048): ler, mexer e
     * gerenciar (equipe da OS, texto da garantia). Nenhuma página do site a
     * declara — o front é o Aeris Tech —, então quem barra é
     * `requireFeature('assistencia')` em cada rota, como `uniforms.write`.
     */
    'repair.read',
    'repair.write',
    'repair.manage',
    /**
     * A visita da climatização (F331, ADR-0049): ler, mexer e gerenciar
     * (equipe, tabela de preços, modelos, o contrato do PMOC com a ART/TRT
     * anexada), e `field.pmoc`, o aceite do plano e do mês, que é do responsável
     * técnico. O front é o Aeris Bot:
     * quem barra é `requireFeature('climatizacao')` em cada rota.
     */
    'field.read',
    'field.write',
    'field.manage',
    'field.pmoc',
    /**
     * O ponto dos funcionários (F339, ADR-0050): ver quem está na loja, as
     * batidas e as fotos (`read`); cadastrar o código e a foto, parear e revogar
     * aparelhos e marcar a loja no mapa (`manage`). Declarada pela tela Ponto,
     * com a chave `ponto`.
     */
    'timeclock.read',
    'timeclock.manage',
    /**
     * A nota fiscal (F371, ADR-0051): ver as notas (`read`), emitir (`emit`),
     * cancelar e corrigir (`cancel`), e cuidar do certificado, do regime e do
     * ambiente (`configure`). Declarada pela tela, com a chave `fiscal`. O gerente
     * emite e cancela; certificado e ambiente são de owner e admin.
     */
    'fiscal.read',
    'fiscal.emit',
    'fiscal.cancel',
    'fiscal.configure',
    /**
     * A loja online da conta (F221): o endereço, a filial que ela vende e as
     * sessões abertas dos lojistas. Owner, admin e gerente — quem responde pela
     * loja. As rotas exigem também a chave `loja-online`.
     */
    'storefront.manage',
    /**
     * Perguntar ao assistente (F251–F255): pelo WhatsApp do assistente ou pelo
     * Claude (MCP). Owner, admin e gerente. Ele só responde o que as OUTRAS
     * permissões da pessoa já deixam ver nas telas — esta só abre a porta.
     * Declarada pela tela "Meu assistente", com a chave `assistente`: sem a
     * chave, ou com a tela negada à pessoa (F038), ela some.
     */
    'assistant.use',
    /**
     * Configurar o assistente e ler o histórico de perguntas de todos (F254).
     * Owner e admin.
     */
    'assistant.manage',
];
/**
 * Access-control roles.
 *
 * Not to be confused with the *party* roles in Master Specification section 10
 * (Customer, Supplier, Carrier…), which describe what an organisation is to the
 * business and have nothing to do with access.
 *
 * The set is fixed and global. Per-tenant custom roles are a later feature, and
 * ADR-0013 explains why that is an internal change rather than a rewrite.
 *
 * **A ordem é a hierarquia.** `authority.ts` deriva o rank do índice, e é ele
 * que decide quem pode conceder um papel e quem pode agir sobre quem.
 *
 * `operator` foi aposentado pela ADR-0027, e não substituído por um sinônimo: o
 * balcão tem dois lugares, e "operador" descrevia uma pessoa genérica atrás
 * dele. O caixa fica acima do vendedor porque quem responde pelo dinheiro
 * responde por quem vende.
 *
 * O `trocador` (F126) fica entre os dois: ele mexe em estoque e em saldo do
 * cliente, que é mais do que o vendedor toca e menos do que o caixa, que
 * responde pela gaveta.
 */
export const ROLES = [
    'owner',
    'admin',
    'manager',
    'cashier',
    'trocador',
    'salesperson',
];
/**
 * What each role may do.
 *
 * Read this table as the answer to "what can an admin actually do?" — it is
 * meant to be reviewable in a diff, which is the whole point of keeping it in
 * one place rather than scattered across `if` statements.
 *
 * `owner` is not special-cased anywhere in the code: it holds every permission
 * because it is listed as holding every permission. A role that bypasses the
 * check would be a second, invisible authorisation model.
 */
const ROLE_PERMISSIONS = {
    // Written out rather than derived from PERMISSIONS: `owner` holding
    // everything must be a fact visible in a diff, not a spread operator that
    // silently grants each future permission to whoever wrote it.
    owner: [
        'organization.read',
        'organization.write',
        'catalog.read',
        'catalog.write',
        'party.read',
        'party.create',
        'party.write',
        'tag.read',
        'tag.manage',
        'pricing.read',
        'pricing.write',
        'inventory.read',
        'inventory.write',
        'inventory.adjust',
        'sales.read',
        'sales.write',
        'sales.deposit',
        'sales.cancel',
        'sales.amend',
        'sales.check.stock',
        'sales.check.cashier',
        'sales.check.correct',
        'sales.price.override',
        'exchange.read',
        'exchange.request',
        'exchange.manage',
        'exchange.without_sale',
        'exchange.repeat_return',
        'exchange.credit.adjust',
        'purchasing.read',
        'purchasing.write',
        'purchasing.receive',
        'finance.read',
        'delivery.route.read',
        'delivery.route',
        'finance.write',
        'finance.settle',
        'finance.credit.manage',
        'finance.collect',
        'pos.operate',
        'pos.supervise',
        'payment.read',
        'payment.write',
        'payment.method.manage',
        'identity.user.read',
        'identity.user.write',
        'identity.session.revoke',
        'channel.read',
        'channel.write',
        'edge.sync',
        'edge.manage',
        'communication.channel.read',
        'communication.channel.write',
        'communication.conversation.read',
        'communication.message.send',
        'pix.charge.create',
        'pix.charge.notify',
        'pix.charge.read',
        'communication.contact.match',
        'communication.queue.read',
        'communication.queue.write',
        'communication.group.manage',
        'communication.conversation.assign',
        'communication.ticket.read',
        'communication.ticket.write',
        'communication.template.read',
        'communication.template.manage',
        'communication.bot.read',
        'communication.bot.manage',
        'communication.followup.read',
        'communication.followup.manage',
        'communication.campaign.read',
        'communication.campaign.manage',
        'communication.cost.read',
        'dashboard.read',
        'report.read',
        'commission.read',
        'commission.manage',
        'commission.read.own',
        'expense.read',
        'expense.write',
        'expense.manage',
        'payroll.read',
        'payroll.manage',
        'uniforms.read',
        'uniforms.write',
        'uniforms.manage',
        'repair.read',
        'repair.write',
        'repair.manage',
        'field.read',
        'field.write',
        'field.manage',
        'field.pmoc',
        'timeclock.read',
        'timeclock.manage',
        'fiscal.read',
        'fiscal.emit',
        'fiscal.cancel',
        'fiscal.configure',
        'storefront.manage',
        'assistant.use',
        'assistant.manage',
    ],
    admin: [
        'organization.read',
        'organization.write',
        'catalog.read',
        'catalog.write',
        'party.read',
        'party.create',
        'party.write',
        'tag.read',
        'tag.manage',
        'pricing.read',
        'pricing.write',
        'inventory.read',
        'inventory.write',
        'inventory.adjust',
        'sales.read',
        'sales.write',
        'sales.deposit',
        'sales.cancel',
        'sales.amend',
        'sales.check.stock',
        'sales.check.cashier',
        'sales.check.correct',
        'sales.price.override',
        'exchange.read',
        'exchange.request',
        'exchange.manage',
        'exchange.without_sale',
        'exchange.repeat_return',
        'exchange.credit.adjust',
        'purchasing.read',
        'purchasing.write',
        'purchasing.receive',
        'finance.read',
        'delivery.route.read',
        'delivery.route',
        'finance.write',
        'finance.settle',
        'finance.credit.manage',
        'finance.collect',
        'pos.operate',
        'pos.supervise',
        'payment.read',
        'payment.write',
        'payment.method.manage',
        'identity.user.read',
        'identity.user.write',
        'identity.session.revoke',
        'channel.read',
        'channel.write',
        'edge.sync',
        'edge.manage',
        'communication.channel.read',
        'communication.channel.write',
        'communication.conversation.read',
        'communication.message.send',
        'pix.charge.create',
        'pix.charge.notify',
        'pix.charge.read',
        'communication.contact.match',
        'communication.queue.read',
        'communication.queue.write',
        'communication.group.manage',
        'communication.conversation.assign',
        'communication.ticket.read',
        'communication.ticket.write',
        'communication.template.read',
        'communication.template.manage',
        'communication.bot.read',
        'communication.bot.manage',
        'communication.followup.read',
        'communication.followup.manage',
        'communication.campaign.read',
        'communication.campaign.manage',
        'communication.cost.read',
        'dashboard.read',
        'report.read',
        'commission.read',
        'commission.manage',
        'commission.read.own',
        'expense.read',
        'expense.write',
        'expense.manage',
        'payroll.read',
        'payroll.manage',
        'uniforms.read',
        'uniforms.write',
        'uniforms.manage',
        'repair.read',
        'repair.write',
        'repair.manage',
        'field.read',
        'field.write',
        'field.manage',
        'field.pmoc',
        'timeclock.read',
        'timeclock.manage',
        'fiscal.read',
        'fiscal.emit',
        'fiscal.cancel',
        'fiscal.configure',
        'storefront.manage',
        'assistant.use',
        'assistant.manage',
    ],
    /**
     * A manager runs a branch.
     *
     * They maintain the catalogue, prices, stock and customers, and can cancel a
     * sale — but they do not administer users or the organisation itself, which
     * is what separates running a shop from owning the account.
     */
    manager: [
        'organization.read',
        /*
         * Configurar a loja é trabalho de quem a dirige (F035).
         *
         * O gerente já decide preço, estoque e o que é vendido; o momento em que a
         * peça sai do estoque e os dados que saem no documento fiscal são a mesma
         * espécie de decisão. Sem isto, a tela da Empresa ficaria só com o dono e o
         * administrador, e a pergunta "quem configura a loja?" não teria resposta
         * na própria loja.
         */
        'organization.write',
        'catalog.read',
        'catalog.write',
        'party.read',
        'party.create',
        'party.write',
        'tag.read',
        'tag.manage',
        'pricing.read',
        'pricing.write',
        'inventory.read',
        'inventory.write',
        'inventory.adjust',
        'sales.read',
        'sales.write',
        'sales.deposit',
        'sales.cancel',
        'sales.amend',
        'sales.check.stock',
        'sales.check.cashier',
        'sales.check.correct',
        'sales.price.override',
        'exchange.read',
        'exchange.request',
        'exchange.manage',
        'exchange.without_sale',
        'exchange.repeat_return',
        'purchasing.read',
        'purchasing.write',
        'purchasing.receive',
        'finance.read',
        'delivery.route.read',
        'delivery.route',
        'finance.write',
        'finance.settle',
        'finance.credit.manage',
        'finance.collect',
        'pos.operate',
        'pos.supervise',
        'payment.read',
        'payment.write',
        'channel.read',
        'channel.write',
        // A manager runs a branch, and the branch's Edge is part of the branch.
        'edge.sync',
        'edge.manage',
        // Só leitura (F045): quem administra a conta de canal é quem responde
        // por ela, não quem roda a loja no dia a dia.
        'communication.channel.read',
        // O inbox em si (F046) é diferente: um manager de loja atende e
        // responde como qualquer um do balcão, mesmo sem poder reconfigurar a
        // conta de canal acima.
        'communication.conversation.read',
        'communication.message.send',
        'pix.charge.create',
        'pix.charge.notify',
        'pix.charge.read',
        // Um manager decide quem é o cliente do outro lado tanto quanto qualquer
        // outro atendente (F048).
        'communication.contact.match',
        // Filas e membros SÃO administração de loja (F047) — diferente do
        // atendimento em si, que qualquer balcão faz.
        'communication.queue.read',
        'communication.queue.write',
        'communication.group.manage',
        'communication.conversation.assign',
        // Administra a operação da loja — inclusive os chamados que o balcão
        // abre (F051).
        'communication.ticket.read',
        'communication.ticket.write',
        // Modelos (F050) TAMBÉM são administração de loja: a categoria de um
        // modelo decide o custo de toda campanha que o usar depois — não é
        // decisão de balcão, mas um manager roda a operação.
        'communication.template.read',
        'communication.template.manage',
        'communication.bot.read',
        'communication.bot.manage',
        // Follow-up (F053) é do mesmo tamanho que o robô e os modelos: uma
        // decisão de como a loja fala com o cliente meses depois de uma venda,
        // não trabalho de balcão.
        'communication.followup.read',
        'communication.followup.manage',
        // Campanhas e custos (F054) — mesma disciplina de follow-up e robô: um
        // manager roda a operação da loja, inclusive quem ela avisa em massa e
        // quanto isso custa.
        'communication.campaign.read',
        'communication.campaign.manage',
        'communication.cost.read',
        'dashboard.read',
        'report.read',
        'commission.read',
        'commission.read.own',
        'expense.read',
        'expense.write',
        'expense.manage',
        'uniforms.read',
        'uniforms.write',
        'uniforms.manage',
        'repair.read',
        'repair.write',
        'repair.manage',
        'field.read',
        'field.write',
        'field.manage',
        'field.pmoc',
        'timeclock.read',
        'timeclock.manage',
        'fiscal.read',
        'fiscal.emit',
        'fiscal.cancel',
        'storefront.manage',
        'assistant.use',
    ],
    /**
     * O caixa recebe (ADR-0027).
     *
     * É o antigo `operator` dito com precisão, mais o dinheiro que volta da rua.
     * Ele vende, recebe, abre a gaveta e registra o cliente que está na frente
     * dele — `party.create` sim, `party.write` não: criar um cadastro é trabalho
     * de balcão, reescrever o documento de um cliente que já existe não é.
     *
     * `finance.read` e `finance.settle` são a novidade, e são o acerto do
     * motoboy: *"a caixa depois atualiza e dá baixa de motoboy por motoboy"*. Sem
     * elas, a tela do acerto é uma tela que ele não pode abrir — e o trabalho é
     * dele. `finance.write` fica de fora: baixar um título que já existe é
     * receber; criar e cancelar títulos é outra conversa, e é do gerente.
     *
     * **Não** tem `sales.price.override`, e a razão está na decisão 3 da
     * ADR-0027: quem recebe o dinheiro não muda o número depois de o cliente ter
     * concordado com ele.
     */
    cashier: [
        'organization.read',
        'catalog.read',
        'party.read',
        'tag.read',
        'party.create',
        'pricing.read',
        'inventory.read',
        'sales.read',
        'sales.write',
        'sales.deposit',
        'sales.check.cashier',
        // Pedir, não fazer (F126): o caixa atende quem voltou com a peça e manda
        // para o trocador. Quem mexe no estoque e no saldo é outra pessoa.
        'exchange.read',
        'exchange.request',
        // Reading only. A cashier sees what a product cost so they can answer a
        // question at the counter; ordering and receiving belong to whoever
        // negotiates with suppliers.
        'purchasing.read',
        'pos.operate',
        'payment.read',
        'payment.write',
        'finance.read',
        'delivery.route.read',
        'delivery.route',
        'finance.settle',
        'finance.collect',
        'commission.read.own',
        // A menina do caixa preenche a folha e anota os gastos (F146). Quem define
        // salário, funcionário e categoria é o gerente (`expense.manage`).
        'expense.read',
        'expense.write',
        // O atendimento por WhatsApp é balcão (F046) — é o pedido do dono: o
        // caixa também atende, do mesmo jeito que já vende e recebe.
        'communication.conversation.read',
        'communication.message.send',
        'pix.charge.create',
        'pix.charge.notify',
        'pix.charge.read',
        // E identificar quem está do outro lado é parte do mesmo atendimento
        // (F048) — o caixa que já registra o cliente no balcão (`party.create`)
        // confirma o mesmo cliente quando ele escreve pelo WhatsApp.
        'communication.contact.match',
        // Ver as filas para escolher uma ao assumir uma conversa, e assumir/
        // atribuir (F047) — mesma lógica de balcão de `.conversation.read`.
        // NÃO `.queue.write`: o caixa não cria nem reconfigura filas.
        'communication.queue.read',
        'communication.conversation.assign',
        // Chamados (F051) — mesma lógica de balcão: quem atende no WhatsApp
        // também abre e trabalha o chamado que sobrou da conversa.
        'communication.ticket.read',
        'communication.ticket.write',
        // Só leitura (F050): o caixa escolhe um modelo aprovado para enviar
        // fora da janela de 24h, mas não cria nem submete um novo à Meta.
        'communication.template.read',
    ],
    /**
     * O trocador troca (F126).
     *
     * Recebe o pedido do caixa ou atende direto, confere a venda original, marca o
     * que voltou — sempre como defeito, que não volta ao estoque (F145) — e
     * conclui: troca por outra peça, no máximo na quantidade devolvida, ou vale.
     * O comprovante sai impresso.
     *
     * NÃO tem `pos.operate`, `payment.*` nem `finance.settle`: a diferença que o
     * cliente deve vira título, e quem recebe é o caixa. Trocar e receber pela
     * mesma pessoa seria desfazer a separação que a ADR-0027 fez no balcão.
     */
    trocador: [
        'organization.read',
        'catalog.read',
        'party.read',
        'party.create',
        'tag.read',
        'pricing.read',
        'inventory.read',
        // A venda original é o ponto de partida de toda troca.
        'sales.read',
        'sales.check.stock',
        'exchange.read',
        'exchange.manage',
        'commission.read.own',
    ],
    /**
     * O vendedor promete (ADR-0027).
     *
     * Ele atende, monta o orçamento e o imprime — e o papel impresso segura a
     * mercadoria. O cliente atravessa a loja com ele e outra pessoa recebe.
     *
     * O que ele NÃO tem é a lista inteira do dinheiro: `pos.operate`,
     * `payment.*`, `finance.*` e `sales.cancel`. Enquanto vender e receber forem
     * a mesma pessoa, a travessia do salão é teatro — uma etapa que o sistema
     * encena e não exige.
     *
     * `sales.price.override` é dele e não do caixa. Parece invertido e não é:
     * quem está com o cliente na frente é quem discute preço, e é o que a loja já
     * faz.
     */
    salesperson: [
        'organization.read',
        'catalog.read',
        'party.read',
        'tag.read',
        'party.create',
        'pricing.read',
        'inventory.read',
        'sales.read',
        'sales.write',
        'sales.deposit',
        'sales.price.override',
        // Mesma razão do caixa: o vendedor também atende quem voltou.
        'exchange.read',
        'exchange.request',
        // Mesma razão do caixa: o atendimento por WhatsApp é balcão, e o
        // vendedor é quem mais fala com o cliente fora da loja física.
        'communication.conversation.read',
        'communication.message.send',
        'pix.charge.create',
        'communication.contact.match',
        // Mesma razão do caixa acima (F047).
        'communication.queue.read',
        'communication.conversation.assign',
        // Chamados (F051) — mesma lógica de balcão: quem atende no WhatsApp
        // também abre e trabalha o chamado que sobrou da conversa.
        'communication.ticket.read',
        'communication.ticket.write',
        // Só leitura (F050) — mesma razão do caixa acima.
        'communication.template.read',
        // A própria comissão (F137), e só ela.
        'commission.read.own',
        // A fila de produção (F170): quem trabalha nela troca a etapa. O cadastro
        // de impressoras (`uniforms.manage`) fica com quem dirige a loja.
        'uniforms.read',
        'uniforms.write',
        // A OS (F321): o vendedor abre, anda e entrega; a equipe da OS e o texto
        // da garantia (`repair.manage`) ficam com quem dirige a loja.
        'repair.read',
        'repair.write',
        // A visita (F331): o vendedor agenda, atende e orça; preços, equipe e o
        // aceite do PMOC ficam com quem dirige a empresa.
        'field.read',
        'field.write',
    ],
};
/**
 * Every permission granted to a role. Ordered as declared, never mutated.
 *
 * The cast is deliberate: the parameter is typed as `Role`, but the value
 * reaching this function comes from a database column, and a row written by an
 * older or newer version of the code can hold something outside the union. The
 * lookup has to be able to miss.
 */
export const permissionsForRole = (role) => ROLE_PERMISSIONS[role] ??
    [];
/**
 * Deny by default.
 *
 * An unknown role or an unknown permission denies. That is the whole safety
 * property: a typo in a permission name locks a feature instead of opening it,
 * and a role that has not been taught about a new module cannot use it by
 * accident. Master Specification section 28.
 */
export const can = (role, permission) => {
    if (role === null || role === undefined)
        return false;
    const granted = ROLE_PERMISSIONS[role];
    return granted?.includes(permission) ?? false;
};
/** Narrows an untrusted string, so a stale database value cannot become a role. */
export const isRole = (value) => typeof value === 'string' && ROLES.includes(value);
export const isPermission = (value) => typeof value === 'string' &&
    PERMISSIONS.includes(value);
//# sourceMappingURL=catalogue.js.map